import dynamic from 'next/dynamic';
import type { FC, ReactNode } from 'react';
import { useRouter } from 'next/router';
import Container from '@mui/material/Container';
import LinearProgress from '@mui/material/LinearProgress';

import type { GeoCountryCode } from '@packages/config/src/default';
import { useRouterTransition, useDebugModeActivation } from '@packages/shared';
import { useCookies, useConfig } from '@packages/utilities';

import { Header, Footer, PromotionBanner } from '@packages/modules';

const DynamicTestPanel = dynamic(
  () => import('@packages/shared/src/components/TestPanel/TestPanel'),
  {
    ssr: false,
  },
);

const DynamicGeoCountryLayer = dynamic(
  () =>
    import(
      /* webpackChunkName: 'CMS_GeoCountryLayer' */ '@packages/cms-components/src/components/GeoCountryLayer/GeoCountryLayer'
    ),
  {
    ssr: false,
  },
);

const DynamicRedirectOverlay = dynamic(
  () => import('@packages/modules/src/RedirectOverlay/RedirectOverlay'),
  {
    ssr: false,
  },
);

export interface PageLayoutProps {
  geoCountryCode?: GeoCountryCode;
  /** React children nodes */
  children?: ReactNode;
}

const PageTransitionLoadingBar: FC = () => {
  const { isTransitioning } = useRouterTransition();

  if (!isTransitioning) return null;

  return (
    <LinearProgress
      sx={{
        position: 'fixed',
        top: '0',
        width: '100vw',
        height: '3px',
        zIndex: 'tooltip',
      }}
      color="primary"
    />
  );
};

const TestPanelWrapper: FC = () => {
  const router = useRouter();
  const { query } = router;

  return (
    typeof window !== 'undefined' &&
    window.location.href.indexOf('empirie.dev') > 0 &&
    !query.skipTP && <DynamicTestPanel />
  );
};

/**
 * Global PageLayout component which includes Header, PromotionBanner, and Footer
 * */
export const PageLayout = ({ geoCountryCode, children }: PageLayoutProps) => {
  const {
    redirectOverlay: { enabled: redirectOverlayEnabled },
  } = useConfig();
  const { getCookies } = useCookies();
  const cookies = getCookies();
  const isApp = cookies.appView === 'true';

  useDebugModeActivation();

  return (
    <>
      <TestPanelWrapper />
      <PageTransitionLoadingBar />
      {!isApp && <Header />}
      {!isApp && <PromotionBanner />}
      <Container component="main" sx={{ py: 3 }}>
        {children}
      </Container>
      {!isApp && geoCountryCode && <DynamicGeoCountryLayer geoCountryCode={geoCountryCode} />}
      {!isApp && redirectOverlayEnabled && <DynamicRedirectOverlay />}
      {!isApp && <Footer />}
    </>
  );
};
